import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ServiceLanding, buildLandingSchemas } from "@/components/ServiceLanding";
import { canonicalAndHreflangEn, ctaDesc, ctaTitle } from "@/lib/seo";
import { PAN_SCENARIOS } from "@/data/pan-card-scenarios";

const TITLE = "India PAN Card Service from Thailand (NRI · OCI · Foreign) | NYC";
const DESC = "Turnkey Indian PAN Card (Form 49A/49AA) service from Thailand for NRI, OCI, foreign individuals. Talk to a specialist today; no obligation, fast reply.";

const steps = [
  { name: "Eligibility check", text: "Confirm purpose (tax, investment, business, inheritance) to pick the correct Form 49A / 49AA." },
  { name: "Document prep", text: "Passport, address proof, photograph, signature — packaged per NSDL / UTIITSL specs." },
  { name: "Translate + Notary + Apostille", text: "Foreign documents require Notary + Apostille (Hague, in force 15 Dec 2025) or Embassy Legalization." },
  { name: "File with NSDL / UTIITSL", text: "Online filing with Protean e-Gov or UTIITSL — 3-day status tracking." },
  { name: "Receive e-PAN + physical card", text: "e-PAN (PDF) in 3–5 days; physical card 15–25 days by Speed Post India." },
];

const faqs = [
  { q: "What is a PAN Card?", a: "Permanent Account Number — a 10-digit tax identifier issued by India's Income Tax Department, required for every financial transaction in India." },
  { q: "Do I need to fly to India?", a: "No — Form 49AA (for NRI, OCI, and foreign nationals) is filed online with NSDL/Protean directly from Thailand. Supporting documents are couriered to the Mumbai or Pune processing centre by DHL Express, and the entire application, tracking, and grievance workflow is handled by our Bangkok office end-to-end." },
  { q: "Turnaround?", a: "The e-PAN (digitally signed PDF) is issued 3–5 business days after NSDL clears document verification and is accepted for every official use including bank KYC. The physical laminated card ships from India via Speed Post in a further 15–25 business days; DHL upgrade cuts that to 7–10 days." },
  { q: "Total cost?", a: "Form fee ~₹1,020 (NRI) + translation / Notary / Apostille + NYC service fee — packages from ฿3,500 (reprint) to ฿15,000 (corporate / trust)." },
  { q: "Do I need an Indian address?", a: "Not required for NRI, OCI cardholders, or foreign nationals filing Form 49AA — an overseas or Thai address is fully accepted, and the physical PAN card ships to whichever address you list on the form. Address proof from Thailand (yellow tabien baan, work permit, or lease) is translated and Apostilled to meet NSDL evidence rules." },
  { q: "What can I use it for?", a: "Open NRO/NRE accounts, invest in mutual funds/equity via demat, buy/sell property, file ITR, receive dividends/royalty, claim Tax Residency Certificate under India–Thailand DTAA." },
  { q: "Does it expire?", a: "PAN itself never expires and the 10-digit number is issued for life. However, residents must link PAN with Aadhaar (deadline enforced by CBDT) or the PAN becomes 'inoperative' — triggering 20% TDS. Any change of name, date of birth, signature, or address must be updated via Form 49A correction to keep it valid." },
  { q: "Lost PAN?", a: "File Reprint of PAN via Form 49A correction — NYC packages start at ฿3,500 and reissue the exact same 10-digit number on a fresh laminated card in 10–15 business days. If the physical card is lost overseas, an e-PAN duplicate can be emailed within 48 hours as an interim proof for banks and KYC." },
];

export const Route = createFileRoute("/en/pan-card/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/pan-card", "en")}, { name: "description", content: ctaDesc(DESC, "/en/pan-card", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/pan-card", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/pan-card", "en")},
      { property: "og:url", content: "https://nyclanguageinstitute.com/en/pan-card" },
    ],
    links: canonicalAndHreflangEn("/en/pan-card", "/pan-card"),
    scripts: buildLandingSchemas({
      path: "/en/pan-card", title: TITLE, description: DESC,
      serviceType: "PAN Card Application Service (India)", priceFromThb: 3500, steps, faqs,
      breadcrumbs: [{ name: "Home", path: "/en" }, { name: "Services", path: "/en/services" }, { name: "PAN Card", path: "/en/pan-card" }],
    }),
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <ServiceLanding
        badge="India · Income Tax Dept."
        title="India PAN Card — filed from Thailand"
        subtitle={DESC}
        quickAnswer={{
          q: "Can I apply for an Indian PAN Card from Thailand?",
          a: "Yes — NRI, OCI, foreign individuals, companies and trusts file Form 49AA online with NSDL from Thailand, no travel required. From ฿3,500, translation + Notary + Apostille included. e-PAN 3–5 days · physical card 15–25 days.",
        }}
        heroBullets={[
          "Filed entirely from Thailand — no travel needed",
          "Covers every scenario (NRI/OCI/foreign/company/trust/reprint)",
          "Translation + Notary + Apostille bundled",
          "NSDL status tracked every 3 days",
        ]}
        whoFor={{
          th: ["NRIs living in Thailand", "OCI card holders", "Investors in Indian mutual funds / equity", "Thai companies trading with India"],
          foreign: ["Foreign nationals with Indian income", "MNCs opening an Indian branch"],
        }}
        steps={steps}
        pricing={PAN_SCENARIOS.map((s) => ({ label: `${s.en} (Form ${s.form})`, price: `${s.priceFrom.toLocaleString()}+`, note: s.turnaround }))}
        faqs={faqs}
        relatedLinks={[
          { to: "/en/tax", label: "India–Thailand DTAA" },
          { to: "/en/apostille", label: "Apostille service" },
          { to: "/en/notary", label: "Notary Public" },
          { to: "/en/translate", label: "Certified Translation" },
          { to: "/en/services", label: "All services" },
        ]}
      />
      <section className="mx-auto max-w-5xl px-4 py-12">
        <h2 className="text-2xl font-bold">Pick your applicant type</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {PAN_SCENARIOS.map((s) => (
            <Link key={s.slug} to="/en/pan-card/$scenario" params={{ scenario: s.slug }}
              className="rounded border p-4 hover:border-primary">
              <div className="font-semibold">{s.en}</div>
              <div className="text-xs text-muted-foreground mt-1">Form {s.form}</div>
              <div className="text-xs mt-2">From ฿{s.priceFrom.toLocaleString()} · {s.turnaround}</div>
            </Link>
          ))}
        </div>
      </section>
    <RelatedHubGrid locale="en" />
    </>
  );
}
